import React, { useEffect, useState } from 'react';
import { FormLabel, Icon } from '@chakra-ui/core';
import { useSelector } from 'react-redux';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { apiV1 } from '../common/api/api';
import { Funders } from '../common/components/Funders/Funders';
import QuestionFilterSelect from './QuestionFilterSelect';
import QuoteQuestionDetails from './QuoteQuestionDetails';
import QuestionLoading from './QuestionLoading';
import {
  canBeQuestionRespondent,
  canManageQuestions,
  initials,
  questionDelay,
  questionState,
} from './questionHelpers';
import './QuestionInterface.css';

const cards = [
  {
    key: 'all',
    label: 'Toutes les questions',
    help: 'Une vue sur tous vos échanges',
    icon: 'comment',
  },
  { key: 'open', label: 'À traiter', help: 'Une réponse de votre part est attendue', icon: 'edit' },
  { key: 'processed', label: 'Traitées', help: 'Les réponses restent consultables', icon: 'check' },
];
const listTitles = {
  all: 'Tous les échanges',
  open: 'Questions à traiter',
  processed: 'Questions traitées',
  other: 'Échanges antérieurs',
};
const rowStatuses = { open: 'À traiter', processed: 'Traitée', other: 'Antérieur' };
const statusOptions = [
  { value: 'all', label: 'Toutes les questions' },
  { value: 'open', label: 'En attente de réponse' },
  { value: 'processed', label: 'Traitées' },
];
const periodOptions = [
  { value: '', label: 'Toutes les dates' },
  { value: '7', label: '7 derniers jours' },
  { value: '30', label: '30 derniers jours' },
  { value: '90', label: '90 derniers jours' },
];
const sortOptions = [
  { value: 'newest', label: 'Plus récentes' },
  { value: 'oldest', label: 'Plus anciennes' },
];

const Empty = ({ title, children, icon = 'comment' }) => (
  <div className="qq-empty">
    <span className="qq-empty-icon">
      <Icon name={icon} size="25px" aria-hidden="true" />
    </span>
    <strong>{title}</strong>
    {children}
  </div>
);

const QuoteQuestionInbox = () => {
  const authentication = useSelector(state => state.authentication || {});
  const user = Object.keys(authentication.profile || {}).length
    ? authentication.profile
    : authentication.user;
  const allowed = canManageQuestions(user);
  const location = useLocation();
  const history = useHistory();
  const initial = new URLSearchParams(location.search);
  const [filters, setFilters] = useState({
    status: ['open', 'processed', 'other'].includes(initial.get('status'))
      ? initial.get('status')
      : 'all',
    q: initial.get('q') || '',
    folder: initial.get('folder') || '',
    period: ['7', '30', '90'].includes(initial.get('period')) ? initial.get('period') : '',
    sort: initial.get('sort') === 'oldest' ? 'oldest' : 'newest',
    page: Math.max(1, Number(initial.get('page')) || 1),
  });
  const [result, setResult] = useState(null);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    const timer = setTimeout(() => {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== ''),
      );
      history.replace({
        pathname: location.pathname,
        search: new URLSearchParams(params).toString(),
      });
      setPending(true);
      setError(false);
      apiV1
        .get('/quote_ticket_inbox', { params })
        .then(({ data }) => {
          if (!active) return;
          const tickets = data.quoteTickets || data.quote_tickets || [];
          setResult({ tickets, meta: data.meta });
          setSelected(
            current =>
              tickets.find(ticket => ticket.id === current?.id) || current || tickets[0] || null,
          );
        })
        .catch(() => {
          if (active) setError(true);
        })
        .finally(() => {
          if (active) setPending(false);
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [allowed, filters, refresh, history, location.pathname]);

  useEffect(() => {
    const hasDraft = Object.values(drafts).some(value => value.trim());
    if (!hasDraft) return undefined;
    const confirmLeave = event => {
      event.preventDefault();
      const leaveEvent = event;
      leaveEvent.returnValue = '';
    };
    window.addEventListener('beforeunload', confirmLeave);
    return () => window.removeEventListener('beforeunload', confirmLeave);
  }, [drafts]);

  const changeFilter = (key, value) => {
    setError(false);
    setPending(true);
    setFilters(current => ({ ...current, [key]: value, page: key === 'page' ? value : 1 }));
    setSelected(null);
    setDetailOpen(false);
  };
  const resetFilters = () => {
    setError(false);
    setPending(true);
    setFilters({ status: 'all', q: '', folder: '', period: '', sort: 'newest', page: 1 });
    setSelected(null);
    setDetailOpen(false);
  };
  const refreshQuestions = () => {
    setError(false);
    setPending(true);
    setRefresh(value => value + 1);
  };
  const tickets = result?.tickets || [];
  const meta = result?.meta;
  const totalPages = meta?.totalPages || meta?.total_pages || 1;
  const hasFilters = filters.status !== 'all' || filters.q || filters.folder || filters.period;
  const activeFilters = [
    filters.status !== 'all' && { key: 'status', label: listTitles[filters.status], reset: 'all' },
    filters.q && { key: 'q', label: `Recherche : ${filters.q}`, reset: '' },
    filters.folder && { key: 'folder', label: `Dossier : ${filters.folder}`, reset: '' },
    filters.period && { key: 'period', label: `${filters.period} derniers jours`, reset: '' },
  ].filter(Boolean);

  const cardCount = key => (error || !meta ? '—' : meta.counts[key]);
  let resultLabel = `${meta?.total || 0} résultat${meta?.total === 1 ? '' : 's'}`;
  if (pending) resultLabel = 'Chargement…';
  if (error) resultLabel = 'Indisponible';
  const renderList = () => {
    if (error)
      return (
        <Empty title="Chargement impossible" icon="warning">
          <p role="alert">Les questions n’ont pas pu être chargées.</p>
          <button className="qq-button" type="button" onClick={refreshQuestions}>
            Réessayer
          </button>
        </Empty>
      );
    if (pending && (!result || !tickets.length))
      return <QuestionLoading variant="list" label="Chargement des questions" />;
    if (!tickets.length)
      return (
        <Empty
          title={hasFilters ? 'Aucune question ne correspond' : 'Aucune question pour le moment'}
        >
          <p>
            {hasFilters
              ? 'Essayez une autre recherche ou élargissez vos filtres.'
              : 'Les questions des entreprises apparaîtront ici.'}
          </p>
          {hasFilters && (
            <button className="qq-button" type="button" onClick={resetFilters}>
              Effacer les filtres
            </button>
          )}
        </Empty>
      );
    return tickets.map(ticket => {
      const state = questionState(ticket);
      const delay = questionDelay(ticket);
      return (
        <button
          key={ticket.id}
          className="qq-ticket"
          type="button"
          disabled={pending}
          aria-current={selected?.id === ticket.id}
          aria-label={`Ouvrir la question ${ticket.id} : ${ticket.question}`}
          onClick={() => {
            setSelected(ticket);
            setDetailOpen(true);
          }}
        >
          <span className="qq-ticket-top">
            <span className="qq-folder-number">
              <Icon name="folder" size="12px" aria-hidden="true" />
              Dossier {ticket.folderFormattedId}
            </span>
            <span
              className={`qq-status qq-status-${state}${delay ? ` ${delay.className}` : ''}`}
              title={delay?.label}
            >
              {rowStatuses[state]}
            </span>
          </span>
          <span className="qq-ticket-title">{ticket.question}</span>
          <span className="qq-ticket-recipient">{ticket.folderRecipientName}</span>
          <span className="qq-ticket-bottom">
            <span className="qq-avatar qq-avatar-small">
              {initials(ticket.createdBy?.fullName)}
            </span>
            <span>{ticket.createdBy?.fullName || 'Auteur'}</span>
            <time>{(ticket.createdAt || '').split(' à ')[0]}</time>
          </span>
          {drafts[ticket.id]?.trim() && (
            <span className="qq-status qq-status-open" style={{ marginTop: 10 }}>
              Brouillon en cours
            </span>
          )}
        </button>
      );
    });
  };

  if (!allowed)
    return (
      <div className="qq-page">
        {canBeQuestionRespondent(user) ? (
          <Empty title="Espace réservé aux répondants désignés" icon="lock">
            <p>
              Votre compte n’est pas désigné pour répondre aux questions des entreprises. Vous
              pouvez consulter les échanges depuis l’historique de chaque dossier.
            </p>
          </Empty>
        ) : (
          <Empty title="Espace réservé à BelAvie" icon="lock">
            <p>Vous pouvez consulter vos échanges depuis l’historique de chaque dossier.</p>
          </Empty>
        )}
      </div>
    );

  return (
    <div
      className={`qq-page${detailOpen ? ' qq-page-detail' : ''}${
        hasFilters ? ' qq-page-filtered' : ''
      }`}
    >
      <Prompt
        when={Object.values(drafts).some(value => value.trim())}
        message={nextLocation =>
          nextLocation.pathname === location.pathname ||
          'Une réponse est encore en cours de rédaction. Quitter cet écran et perdre le brouillon ?'
        }
      />
      <header className="qq-heading">
        <div className="qq-title-group">
          <span className="qq-heading-symbol" aria-hidden="true">
            <Icon name="comment" size="28px" />
            <span />
          </span>
          <div>
            <div className="qq-eyebrow">Relation entreprises</div>
            <h1>
              Questions entreprises
              <span className="qq-title-dot" aria-hidden="true">
                .
              </span>
            </h1>
            <p className="qq-subtitle">
              Chaque question, son contexte. Chaque réponse, au bon endroit.
            </p>
          </div>
        </div>
        <div className="qq-heading-action">
          <span className="qq-heading-tag">
            <Icon name="lock" size="12px" aria-hidden="true" /> Espace BelAvie
          </span>
          <button
            className="qq-button"
            type="button"
            aria-label="Actualiser les questions"
            disabled={pending}
            onClick={refreshQuestions}
          >
            <Icon name="repeat" className={pending ? 'qq-spinning' : ''} aria-hidden="true" />
            {pending ? 'Actualisation…' : 'Actualiser'}
          </button>
        </div>
      </header>
      <div className="qq-stats" aria-label="Filtrer par statut">
        {cards.map(card => (
          <button
            className="qq-stat"
            key={card.key}
            type="button"
            aria-pressed={filters.status === card.key}
            onClick={() => changeFilter('status', card.key)}
          >
            <span className="qq-stat-icon">
              <Icon name={card.icon} size="19px" aria-hidden="true" />
            </span>
            <span className="qq-stat-copy">
              <span className="qq-stat-label">{card.label}</span>
              <span className="qq-stat-help">{card.help}</span>
            </span>
            <span className="qq-stat-count">
              <span key={cardCount(card.key)}>{cardCount(card.key)}</span>
              <small>question{cardCount(card.key) === 1 ? '' : 's'}</small>
            </span>
          </button>
        ))}
      </div>
      <section className="qq-toolbar" aria-label="Recherche et filtres des questions">
        <div className="qq-field qq-search-field">
          <FormLabel htmlFor="questions-search">Rechercher dans les échanges</FormLabel>
          <div className="qq-search-wrap">
            <Icon name="search" size="18px" aria-hidden="true" />
            <input
              className="qq-input"
              id="questions-search"
              type="search"
              value={filters.q}
              placeholder="Question, réponse, auteur, bénéficiaire…"
              onChange={event => changeFilter('q', event.target.value)}
            />
          </div>
        </div>
        <div className="qq-field qq-status-field">
          <FormLabel htmlFor="questions-status">Statut</FormLabel>
          <QuestionFilterSelect
            id="questions-status"
            value={filters.status}
            options={
              meta?.counts?.other > 0 || filters.status === 'other'
                ? [...statusOptions, { value: 'other', label: 'Échanges antérieurs' }]
                : statusOptions
            }
            onChange={value => changeFilter('status', value)}
          />
        </div>
        <div className="qq-field">
          <FormLabel htmlFor="questions-folder">Dossier</FormLabel>
          <input
            className="qq-input"
            id="questions-folder"
            type="search"
            value={filters.folder}
            placeholder="N° de dossier"
            onChange={event => changeFilter('folder', event.target.value)}
          />
        </div>
        <div className="qq-field">
          <FormLabel htmlFor="questions-period">Période</FormLabel>
          <QuestionFilterSelect
            id="questions-period"
            value={filters.period}
            options={periodOptions}
            onChange={value => changeFilter('period', value)}
          />
        </div>
        <div className="qq-field qq-field-sort">
          <FormLabel htmlFor="questions-sort">Trier par</FormLabel>
          <QuestionFilterSelect
            id="questions-sort"
            value={filters.sort}
            options={sortOptions}
            onChange={value => changeFilter('sort', value)}
          />
        </div>
        <button
          className="qq-button qq-reset"
          disabled={!hasFilters}
          aria-label="Réinitialiser les filtres"
          title="Réinitialiser les filtres"
          type="button"
          onClick={resetFilters}
        >
          <Icon name="repeat" aria-hidden="true" />
        </button>
      </section>
      {activeFilters.length > 0 && (
        <div className="qq-active-filters" aria-label="Filtres actifs">
          <span>Vue filtrée</span>
          {activeFilters.map(filter => (
            <button
              className="qq-filter-chip"
              key={filter.key}
              type="button"
              aria-label={`Retirer le filtre ${filter.label}`}
              onClick={() => changeFilter(filter.key, filter.reset)}
            >
              <span>{filter.label}</span>
              <Icon name="close" size="9px" aria-hidden="true" />
            </button>
          ))}
        </div>
      )}
      <div className={`qq-workspace${detailOpen ? ' qq-workspace-detail' : ''}`}>
        <section className="qq-list-panel" aria-label="Liste des questions" aria-busy={pending}>
          {pending && <span className="qq-refresh-indicator" aria-hidden="true" />}
          <div className="qq-list-heading">
            <span className="qq-list-title">
              <Icon name="comment" size="16px" aria-hidden="true" />
              {listTitles[filters.status]}
            </span>
            <span className="qq-result-count" role="status">
              {resultLabel}
            </span>
          </div>
          <div className="qq-list">{renderList()}</div>
          <footer className="qq-pagination">
            <span>
              Page {meta?.page || 1} sur {totalPages}
            </span>
            <div className="qq-pagination-actions">
              <button
                className="qq-button"
                type="button"
                aria-label="Page précédente"
                disabled={pending || !meta || meta.page <= 1}
                onClick={() => changeFilter('page', meta.page - 1)}
              >
                <Icon name="chevron-left" aria-hidden="true" />
              </button>
              <button
                className="qq-button"
                type="button"
                aria-label="Page suivante"
                disabled={pending || !meta || meta.page >= totalPages}
                onClick={() => changeFilter('page', meta.page + 1)}
              >
                <Icon name="chevron-right" aria-hidden="true" />
              </button>
            </div>
          </footer>
        </section>
        <section className="qq-reader" aria-label="Échange sélectionné">
          {selected ? (
            <>
              <div className="qq-reader-heading">
                <button
                  className="qq-button qq-reader-back"
                  type="button"
                  onClick={() => setDetailOpen(false)}
                >
                  <Icon name="arrow-back" aria-hidden="true" />
                  Les questions
                </button>
                <div className="qq-reader-title">
                  <span className="qq-context-icon" aria-hidden="true">
                    <Icon name="folder" size="20px" />
                  </span>
                  <div>
                    <span>Dossier {selected.folderFormattedId}</span>
                    <h2>{selected.folderRecipientName || 'Échange avec l’entreprise'}</h2>
                  </div>
                </div>
                <div className="qq-reader-meta">
                  <span>#{selected.id}</span>
                  <Funders
                    key={selected.folderId}
                    folderId={selected.folderId}
                    trigger={({ onClick }) => (
                      <button className="qq-button" type="button" onClick={onClick}>
                        Ouvrir le dossier
                        <Icon name="external-link" size="12px" aria-hidden="true" />
                      </button>
                    )}
                  />
                </div>
              </div>
              <div className="qq-mobile-context">
                <span>Dossier {selected.folderFormattedId}</span>
                <strong>{selected.folderRecipientName || 'Échange avec l’entreprise'}</strong>
              </div>
              <div className="qq-reader-content">
                <QuoteQuestionDetails
                  key={selected.id}
                  question={selected}
                  folderId={selected.folderId}
                  initialResponse={drafts[selected.id] || ''}
                  onDraftChange={value =>
                    setDrafts(current => ({ ...current, [selected.id]: value }))
                  }
                  onChange={updated => {
                    setSelected(updated);
                    refreshQuestions();
                  }}
                />
              </div>
            </>
          ) : (
            <>
              {pending && !error && <QuestionLoading />}
              {(!pending || error) && (
                <Empty title="Un échange, tout son contexte">
                  <p>
                    Sélectionnez une question pour la consulter
                    <br />
                    et apporter votre réponse à l’entreprise.
                  </p>
                </Empty>
              )}
            </>
          )}
        </section>
      </div>
      <div className="qq-filter-summary">
        <Icon name="info-outline" size="12px" aria-hidden="true" />
        <span>Les compteurs s’adaptent à votre recherche et à vos filtres.</span>
        {meta?.counts?.other > 0 && (
          <button className="qq-tab" type="button" onClick={() => changeFilter('status', 'other')}>
            {meta.counts.other} échange{meta.counts.other > 1 ? 's' : ''} antérieur
            {meta.counts.other > 1 ? 's' : ''}
          </button>
        )}
      </div>
    </div>
  );
};

export default QuoteQuestionInbox;
